import { collection, onSnapshot, query, where, type QuerySnapshot } from 'firebase/firestore';
import React, { createContext, useCallback, useContext, useEffect, useReducer } from 'react';
import { useAuth, useOrganizationId } from './auth/hooks';
import { getDB } from './firebase/config';
import type { Car, Customer, Rental, ServiceLog } from './models';
import { carsApi, customersApi, rentalsApi, servicesApi } from './services/api';
import { logCreate, logDelete, logUpdate } from './utils/audit';

function mapOrgCollectionSnapshot<T extends { id: string }>(snapshot: QuerySnapshot): T[] {
  return snapshot.docs.map((d) => ({ ...d.data(), id: d.id } as T));
}

// Helper function removed - using getDB from config instead

// --- State Types ---
export interface AppState {
  cars: Car[];
  customers: Customer[];
  rentals: Rental[];
  services: ServiceLog[];
  isLoading: boolean;
  error: string | null;
}

export type AppAction =
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_CARS'; cars: Car[] }
  | { type: 'SET_CUSTOMERS'; customers: Customer[] }
  | { type: 'SET_RENTALS'; rentals: Rental[] }
  | { type: 'SET_SERVICES'; services: ServiceLog[] }
  | { type: 'ADD_CAR'; car: Car }
  | { type: 'UPDATE_CAR'; car: Car; previousId?: string }
  | { type: 'DELETE_CAR'; id: string }
  | { type: 'ADD_CUSTOMER'; customer: Customer }
  | { type: 'UPDATE_CUSTOMER'; customer: Customer }
  | { type: 'DELETE_CUSTOMER'; id: string }
  | { type: 'ADD_RENTAL'; rental: Rental }
  | { type: 'UPDATE_RENTAL'; rental: Rental }
  | { type: 'DELETE_RENTAL'; id: string }
  | { type: 'ADD_SERVICE'; service: ServiceLog }
  | { type: 'UPDATE_SERVICE'; service: ServiceLog }
  | { type: 'DELETE_SERVICE'; id: string };

const initialState: AppState = {
  cars: [],
  customers: [],
  rentals: [],
  services: [],
  isLoading: true,
  error: null,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.loading };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.error,
        ...(action.error !== null ? { isLoading: false } : {}),
      };
    case 'SET_CARS':
      return { ...state, cars: action.cars };
    case 'SET_CUSTOMERS':
      return { ...state, customers: action.customers };
    case 'SET_RENTALS':
      return { ...state, rentals: action.rentals };
    case 'SET_SERVICES':
      return { ...state, services: action.services };
    case 'ADD_CAR':
      return { ...state, cars: [...state.cars, action.car] };
    case 'UPDATE_CAR': {
      const previousId = action.previousId ?? action.car.id;
      let insertedUpdatedCar = false;
      const cars = state.cars.reduce<Car[]>((updatedCars, currentCar) => {
        if (currentCar.id === previousId || currentCar.id === action.car.id) {
          if (!insertedUpdatedCar) {
            updatedCars.push(action.car);
            insertedUpdatedCar = true;
          }
        } else {
          updatedCars.push(currentCar);
        }
        return updatedCars;
      }, []);

      if (!insertedUpdatedCar) {
        cars.push(action.car);
      }

      return { ...state, cars };
    }
    case 'DELETE_CAR':
      return { 
        ...state, 
        cars: state.cars.filter(c => c.id !== action.id) 
      };
    case 'ADD_CUSTOMER':
      return { ...state, customers: [...state.customers, action.customer] };
    case 'UPDATE_CUSTOMER':
      return { 
        ...state, 
        customers: state.customers.map(c => c.id === action.customer.id ? action.customer : c) 
      };
    case 'DELETE_CUSTOMER':
      return { 
        ...state, 
        customers: state.customers.filter(c => c.id !== action.id) 
      };
    case 'ADD_RENTAL':
      return { ...state, rentals: [...state.rentals, action.rental] };
    case 'UPDATE_RENTAL':
      return { 
        ...state, 
        rentals: state.rentals.map(r => r.id === action.rental.id ? action.rental : r) 
      };
    case 'DELETE_RENTAL':
      return { 
        ...state, 
        rentals: state.rentals.filter(r => r.id !== action.id) 
      };
    case 'ADD_SERVICE':
      return { ...state, services: [...state.services, action.service] };
    case 'UPDATE_SERVICE':
      return { 
        ...state, 
        services: state.services.map(s => s.id === action.service.id ? action.service : s) 
      };
    case 'DELETE_SERVICE':
      return { 
        ...state, 
        services: state.services.filter(s => s.id !== action.id) 
      };
    default:
      return state;
  }
}

// Context definition
interface AppContextType {
  state: AppState;
  dispatch: (action: AppAction) => void;
  // Firebase CRUD operations
  carOperations: {
    add: (car: Omit<Car, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    update: (id: string, car: Partial<Car>) => Promise<void>;
    delete: (id: string) => Promise<void>;
  };
  customerOperations: {
    add: (customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    update: (id: string, customer: Partial<Customer>) => Promise<void>;
    delete: (id: string) => Promise<void>;
  };
  rentalOperations: {
    add: (rental: Omit<Rental, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    update: (id: string, rental: Partial<Rental>) => Promise<void>;
    delete: (id: string) => Promise<void>;
  };
  serviceOperations: {
    add: (service: Omit<ServiceLog, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    update: (id: string, service: Partial<ServiceLog>) => Promise<void>;
    delete: (id: string) => Promise<void>;
  };
}

const AppContext = createContext<AppContextType>({
  state: initialState,
  dispatch: () => {},
  carOperations: {
    add: async () => {},
    update: async () => {},
    delete: async () => {},
  },
  customerOperations: {
    add: async () => {},
    update: async () => {},
    delete: async () => {},
  },
  rentalOperations: {
    add: async () => {},
    update: async () => {},
    delete: async () => {},
  },
  serviceOperations: {
    add: async () => {},
    update: async () => {},
    delete: async () => {},
  },
});

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { user, isLocalImpersonation } = useAuth();
  const organizationId = useOrganizationId();

  // API CRUD operations
  const carOperations = {
    add: async (carData: Omit<Car, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        const newCar = await carsApi.create(carData);
        
        // Log audit
        await logCreate(user.id, organizationId, 'car', newCar.id);
        
        if (isLocalImpersonation) {
          await loadCars();
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },

    update: async (id: string, carData: Partial<Car>) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        const updatedCar = await carsApi.update(id, carData);
        dispatch({ type: 'UPDATE_CAR', car: updatedCar, previousId: id });
        
        // Log audit
        await logUpdate(user.id, organizationId, 'car', updatedCar.id);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },

    delete: async (id: string) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        await carsApi.delete(id);
        dispatch({ type: 'DELETE_CAR', id });
        
        // Log audit
        await logDelete(user.id, organizationId, 'car', id);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },
  };

  // Track consecutive API failures (local impersonation one-shot loads)
  const authFailureCountRef = React.useRef(0);
  const MAX_AUTH_FAILURES = 3;
  const API_LOAD_STOP_MESSAGE = 'Unable to reach API (network/CORS/auth). Please refresh the page.';

  const isRecoverablePollingFailure = (errorMsg: string): boolean => {
    const normalized = errorMsg.toLowerCase();
    return (
      normalized.includes('not authenticated') ||
      normalized.includes('401') ||
      normalized.includes('network/cors') ||
      normalized.includes('failed to fetch') ||
      normalized.includes('networkerror') ||
      normalized.includes('load failed') ||
      normalized.includes('cors')
    );
  };

  const registerPollingFailure = useCallback((errorMsg: string): boolean => {
    if (!isRecoverablePollingFailure(errorMsg)) {
      return false;
    }
    authFailureCountRef.current += 1;
    if (authFailureCountRef.current >= MAX_AUTH_FAILURES) {
      console.warn('Too many API load failures');
      dispatch({ type: 'SET_ERROR', error: API_LOAD_STOP_MESSAGE });
    }
    return true;
  }, []);

  // Load functions for local impersonation (REST) and mutation refresh
  const loadCars = useCallback(async (): Promise<boolean> => {
    if (!organizationId) return false;
    try {
      const cars = await carsApi.getAll();
      dispatch({ type: 'SET_CARS', cars });
      authFailureCountRef.current = 0; // Reset on success
      return true;
    } catch (error) {
      const errorMsg = (error as Error).message;
      if (registerPollingFailure(errorMsg)) {
        return false;
      }
      // Only log non-auth errors
      console.error('Error loading cars:', error);
      return false;
    }
  }, [organizationId, registerPollingFailure]);

  const loadCustomers = useCallback(async (): Promise<boolean> => {
    if (!organizationId || !user) return false;
    try {
      const customers = await customersApi.getAll();
      dispatch({ type: 'SET_CUSTOMERS', customers });
      authFailureCountRef.current = 0;
      return true;
    } catch (error) {
      const errorMsg = (error as Error).message;
      if (registerPollingFailure(errorMsg)) {
        return false;
      }
      console.error('Error loading customers:', error);
      return false;
    }
  }, [organizationId, registerPollingFailure, user]);

  const loadRentals = useCallback(async (): Promise<boolean> => {
    if (!organizationId || !user) return false;
    try {
      const rentals = await rentalsApi.getAll();
      dispatch({ type: 'SET_RENTALS', rentals });
      authFailureCountRef.current = 0;
      return true;
    } catch (error) {
      const errorMsg = (error as Error).message;
      if (registerPollingFailure(errorMsg)) {
        return false;
      }
      console.error('Error loading rentals:', error);
      return false;
    }
  }, [organizationId, registerPollingFailure, user]);

  const loadServices = useCallback(async (): Promise<boolean> => {
    if (!organizationId || !user) return false;
    try {
      const services = await servicesApi.getAll();
      dispatch({ type: 'SET_SERVICES', services });
      authFailureCountRef.current = 0;
      return true;
    } catch (error) {
      const errorMsg = (error as Error).message;
      if (registerPollingFailure(errorMsg)) {
        return false;
      }
      console.error('Error loading services:', error);
      return false;
    }
  }, [organizationId, registerPollingFailure, user]);

  const customerOperations = {
    add: async (customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        const newCustomer = await customersApi.create(customerData);
        
        // Log audit
        await logCreate(user.id, organizationId, 'customer', newCustomer.id);
        
        if (isLocalImpersonation) {
          await loadCustomers();
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },

    update: async (id: string, customerData: Partial<Customer>) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        const updatedCustomer = await customersApi.update(id, customerData);
        dispatch({ type: 'UPDATE_CUSTOMER', customer: updatedCustomer });
        
        // Log audit
        await logUpdate(user.id, organizationId, 'customer', id);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },

    delete: async (id: string) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        await customersApi.delete(id);
        dispatch({ type: 'DELETE_CUSTOMER', id });
        
        // Log audit
        await logDelete(user.id, organizationId, 'customer', id);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },
  };

  const rentalOperations = {
    add: async (rentalData: Omit<Rental, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        const newRental = await rentalsApi.create({
          ...rentalData,
          // Track who created the booking/rental
          createdBy: user.id,
          createdByRole: user.role,
        });
        
        // Log audit
        await logCreate(user.id, organizationId, 'rental', newRental.id);
        
        if (isLocalImpersonation) {
          await loadRentals();
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },

    update: async (id: string, rentalData: Partial<Rental>) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        const updatedRental = await rentalsApi.update(id, rentalData);
        dispatch({ type: 'UPDATE_RENTAL', rental: updatedRental });
        
        // Log audit
        await logUpdate(user.id, organizationId, 'rental', id);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },

    delete: async (id: string) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        await rentalsApi.delete(id);
        dispatch({ type: 'DELETE_RENTAL', id });
        
        // Log audit
        await logDelete(user.id, organizationId, 'rental', id);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },
  };

  const serviceOperations = {
    add: async (serviceData: Omit<ServiceLog, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        const newService = await servicesApi.create(serviceData);
        
        // Log audit
        await logCreate(user.id, organizationId, 'service', newService.id);
        
        if (isLocalImpersonation) {
          await loadServices();
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },

    update: async (id: string, serviceData: Partial<ServiceLog>) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        const updatedService = await servicesApi.update(id, serviceData);
        dispatch({ type: 'UPDATE_SERVICE', service: updatedService });
        
        // Log audit
        await logUpdate(user.id, organizationId, 'service', id);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },

    delete: async (id: string) => {
      if (!organizationId || !user) {
        throw new Error('User must be authenticated and have an organization');
      }
      try {
        dispatch({ type: 'SET_LOADING', loading: true });
        await servicesApi.delete(id);
        dispatch({ type: 'DELETE_SERVICE', id });
        
        // Log audit
        await logDelete(user.id, organizationId, 'service', id);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        throw error;
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },
  };

  // Load data: Firestore listeners (normal auth) or one-shot API (local impersonation)
  useEffect(() => {
    authFailureCountRef.current = 0;

    if (!organizationId) {
      dispatch({ type: 'SET_CARS', cars: [] });
      dispatch({ type: 'SET_CUSTOMERS', customers: [] });
      dispatch({ type: 'SET_RENTALS', rentals: [] });
      dispatch({ type: 'SET_SERVICES', services: [] });
      dispatch({ type: 'SET_LOADING', loading: false });
      return;
    }

    let cancelled = false;

    if (isLocalImpersonation) {
      if (!user) {
        dispatch({ type: 'SET_CARS', cars: [] });
        dispatch({ type: 'SET_CUSTOMERS', customers: [] });
        dispatch({ type: 'SET_RENTALS', rentals: [] });
        dispatch({ type: 'SET_SERVICES', services: [] });
        dispatch({ type: 'SET_LOADING', loading: false });
        return;
      }

      const loadAll = async () => {
        try {
          dispatch({ type: 'SET_LOADING', loading: true });
          const results = await Promise.all([
            loadCars(),
            loadCustomers(),
            loadRentals(),
            loadServices(),
          ]);
          if (results.some((r) => r === true)) {
            authFailureCountRef.current = 0;
          }
        } catch (error) {
          dispatch({ type: 'SET_ERROR', error: (error as Error).message });
        } finally {
          if (!cancelled) {
            dispatch({ type: 'SET_LOADING', loading: false });
          }
        }
      };

      loadAll();

      return () => {
        cancelled = true;
      };
    }

    const db = getDB();
    const initialSync = {
      cars: false,
      customers: false,
      rentals: false,
      services: false,
    };

    const tryFinishLoading = () => {
      if (
        initialSync.cars &&
        initialSync.customers &&
        initialSync.rentals &&
        initialSync.services
      ) {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    };

    const handleSnapshotError = (err: Error) => {
      console.error('Firestore subscription error:', err);
      dispatch({ type: 'SET_ERROR', error: err.message });
      dispatch({ type: 'SET_LOADING', loading: false });
    };

    dispatch({ type: 'SET_LOADING', loading: true });
    dispatch({ type: 'SET_ERROR', error: null });

    const unsubCars = onSnapshot(
      query(collection(db, 'cars'), where('organizationId', '==', organizationId)),
      (snapshot) => {
        if (cancelled) return;
        dispatch({ type: 'SET_CARS', cars: mapOrgCollectionSnapshot<Car>(snapshot) });
        initialSync.cars = true;
        tryFinishLoading();
      },
      (err) => handleSnapshotError(err),
    );

    const unsubCustomers = onSnapshot(
      query(collection(db, 'customers'), where('organizationId', '==', organizationId)),
      (snapshot) => {
        if (cancelled) return;
        dispatch({ type: 'SET_CUSTOMERS', customers: mapOrgCollectionSnapshot<Customer>(snapshot) });
        initialSync.customers = true;
        tryFinishLoading();
      },
      (err) => handleSnapshotError(err),
    );

    const unsubRentals = onSnapshot(
      query(collection(db, 'rentals'), where('organizationId', '==', organizationId)),
      (snapshot) => {
        if (cancelled) return;
        dispatch({ type: 'SET_RENTALS', rentals: mapOrgCollectionSnapshot<Rental>(snapshot) });
        initialSync.rentals = true;
        tryFinishLoading();
      },
      (err) => handleSnapshotError(err),
    );

    const unsubServices = onSnapshot(
      query(collection(db, 'serviceLogs'), where('organizationId', '==', organizationId)),
      (snapshot) => {
        if (cancelled) return;
        dispatch({ type: 'SET_SERVICES', services: mapOrgCollectionSnapshot<ServiceLog>(snapshot) });
        initialSync.services = true;
        tryFinishLoading();
      },
      (err) => handleSnapshotError(err),
    );

    return () => {
      cancelled = true;
      unsubCars();
      unsubCustomers();
      unsubRentals();
      unsubServices();
    };
  }, [isLocalImpersonation, loadCars, loadCustomers, loadRentals, loadServices, organizationId, user]);

  const contextValue: AppContextType = {
    state,
    dispatch,
    carOperations,
    customerOperations,
    rentalOperations,
    serviceOperations,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

export const useAppContext = () => useContext(AppContext);
